import React, { useState, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  LinearProgress,
  Alert,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  CloudUpload,
  PictureAsPdf,
  Delete,
  Visibility
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { FileInfo, FileUploadType } from '../types';
import { uploadFile, deleteFile } from '../services/storageService';
import { updateUserFile } from '../services/userService';

interface DocumentUploadProps {
  userId: string;
  documentType: 'initialEducationCert' | 'recentEducationCert';
  currentDocument?: FileInfo;
  title: string;
  description?: string;
  onUploadSuccess: (fileInfo: FileInfo) => void;
  onDeleteSuccess: (documentType: 'initialEducationCert' | 'recentEducationCert') => void;
  disabled?: boolean;
}

const DocumentUpload: React.FC<DocumentUploadProps> = ({
  userId,
  documentType,
  currentDocument,
  title,
  description,
  onUploadSuccess,
  onDeleteSuccess,
  disabled = false
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  // 파일 처리 공통 로직
  const processFile = async (file: File, inputElement?: HTMLInputElement) => {
    // 파일 타입 검증
    if (file.type !== 'application/pdf') {
      setError('PDF 파일만 업로드 가능합니다.');
      return;
    }

    // 파일 크기 검증 (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('파일 크기는 10MB를 초과할 수 없습니다.');
      return;
    }

    let progressInterval: NodeJS.Timeout | null = null;
    
    try {
      setUploading(true);
      setError(null);
      setUploadProgress(0);

      console.log('PDF 업로드 시작:', {
        fileName: file.name,
        fileSize: file.size,
        documentType,
        userId,
        hasCurrentDocument: !!currentDocument
      });

      // 진행률 시뮬레이션
      progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      // 기존 파일이 있다면 먼저 삭제
      if (currentDocument?.fileName) {
        console.log('기존 파일 삭제 중:', currentDocument.fileName);
        try {
          await deleteFile(currentDocument.fileName, documentType as FileUploadType);
          console.log('기존 파일 삭제 완료');
        } catch (deleteError) {
          console.warn('기존 파일 삭제 실패 (계속 진행):', deleteError);
        }
      }

      // 파일 업로드
      console.log('Firebase Storage 업로드 시작...');
      const uploadResult = await uploadFile(
        file,
        userId,
        documentType as FileUploadType
      );
      console.log('Firebase Storage 업로드 완료:', uploadResult);

      // 사용자 문서 정보 업데이트
      console.log('Firestore 문서 업데이트 시작...');
      await updateUserFile(userId, documentType, uploadResult);
      console.log('Firestore 문서 업데이트 완료');

      // 성공 처리
      setUploadProgress(100);
      
      // 성공 콜백 호출 (약간의 지연으로 사용자가 완료를 확인할 수 있도록)
      setTimeout(() => {
        onUploadSuccess(uploadResult);
        setUploadSuccess(true);
        setUploading(false);
        setUploadProgress(0);
        console.log('PDF 업로드 전체 프로세스 완료:', uploadResult);
        
        // 3초 후 성공 메시지 숨기기
        setTimeout(() => {
          setUploadSuccess(false);
        }, 3000);
      }, 1000);
      
    } catch (error: any) {
      console.error('PDF 업로드 상세 오류:', {
        error: error,
        message: error.message,
        code: error.code,
        name: error.name,
        stack: error.stack,
        fileName: file.name,
        documentType,
        userId
      });
      
      let errorMessage = '업로드 중 오류가 발생했습니다.';
      
      if (error.code === 'storage/unauthorized') {
        errorMessage = '파일 업로드 권한이 없습니다.';
      } else if (error.code === 'storage/quota-exceeded') {
        errorMessage = '저장 공간이 부족합니다.';
      } else if (error.code === 'storage/invalid-format') {
        errorMessage = '지원하지 않는 파일 형식입니다.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      
      // 에러 발생 시 업로드 상태 즉시 초기화
      setUploading(false);
      setUploadProgress(0);
    } finally {
      // 진행률 인터벌 정리
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      
      // 파일 입력 초기화
      if (inputElement) {
        inputElement.value = '';
      }
    }
  };

  // 파일 업로드 핸들러
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await processFile(file, event.target);
  };

  // 드래그 오버 핸들러
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!disabled && !uploading) {
      setIsDragOver(true);
    }
  };

  // 드래그 리브 핸들러
  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  // 드롭 핸들러
  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);

    if (disabled || uploading) return;

    const files = event.dataTransfer.files;
    if (files.length > 0) {
      await processFile(files[0]);
    }
  };

  // 파일 삭제 핸들러
  const handleFileDelete = async () => {
    if (!currentDocument?.fileName) return;

    try {
      setDeleting(true);
      
      // Storage에서 파일 삭제
      await deleteFile(currentDocument.fileName, documentType as FileUploadType);
      
      // 사용자 문서 정보 업데이트 (null로 설정)
      await updateUserFile(userId, documentType, {
        url: null,
        fileName: null,
        uploadedAt: null
      } as any);

      onDeleteSuccess(documentType);
      setDeleteDialogOpen(false);
    } catch (error: any) {
      setError(error.message || '삭제 중 오류가 발생했습니다.');
    } finally {
      setDeleting(false);
    }
  };

  // 파일 미리보기
  const handleFilePreview = () => {
    if (currentDocument?.url) {
      window.open(currentDocument.url, '_blank');
    }
  };

  // 날짜 포맷팅
  const formatDate = (timestamp: any): string => {
    if (!timestamp) return '';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return format(date, 'yyyy.MM.dd HH:mm', { locale: ko });
    } catch {
      return '';
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Box display="flex" justifyContent="between" alignItems="flex-start" mb={2}>
          <Box>
            <Typography variant="h6" gutterBottom>
              {title}
            </Typography>
            {description && (
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {description}
              </Typography>
            )}
          </Box>
          {currentDocument?.url && (
            <Chip
              size="small"
              icon={<PictureAsPdf />}
              label="PDF"
              color="success"
              variant="outlined"
            />
          )}
        </Box>

        {/* 업로드 진행률 */}
        {uploading && (
          <Box mb={2}>
            <Typography variant="body2" gutterBottom>
              업로드 중... {uploadProgress}%
            </Typography>
            <LinearProgress variant="determinate" value={uploadProgress} />
          </Box>
        )}

        {/* 업로드 완료 메시지 */}
        {uploadSuccess && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setUploadSuccess(false)}>
            <Typography variant="body2" fontWeight="medium">
              ✅ 파일 업로드가 완료되었습니다!
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              파일명: {currentDocument?.fileName}
            </Typography>
          </Alert>
        )}

        {/* 에러 메시지 */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* 현재 파일 정보 */}
        {currentDocument?.url ? (
          <Box>
            <Box 
              display="flex" 
              alignItems="center" 
              justifyContent="between"
              p={2}
              bgcolor="grey.50"
              borderRadius={1}
              mb={2}
            >
              <Box display="flex" alignItems="center" gap={2}>
                {/* PDF 아이콘 및 정보 */}
                <Box 
                  onClick={handleFilePreview}
                  sx={{ 
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 80,
                    height: 100,
                    bgcolor: 'rgba(244, 67, 54, 0.1)',
                    borderRadius: 2,
                    border: '2px dashed rgba(244, 67, 54, 0.3)',
                    '&:hover': {
                      bgcolor: 'rgba(244, 67, 54, 0.15)',
                      borderColor: 'rgba(244, 67, 54, 0.5)',
                      transform: 'scale(1.02)',
                      transition: 'all 0.2s ease'
                    }
                  }}
                >
                  <Box textAlign="center">
                    <PictureAsPdf sx={{ fontSize: 32, color: 'error.main', mb: 0.5 }} />
                    <Typography variant="caption" color="error.main" fontWeight="bold">
                      PDF
                    </Typography>
                  </Box>
                </Box>
                <Box flex={1}>
                  <Typography variant="body2" fontWeight="medium" color="primary">
                    PDF 문서
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    업로드: {formatDate(currentDocument.uploadedAt)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" display="block">
                    📄 클릭하여 미리보기
                  </Typography>
                  <Typography variant="caption" color="primary" display="block" sx={{ mt: 0.5 }}>
                    ✓ 업로드 완료
                  </Typography>
                </Box>
              </Box>
              
              <Box display="flex" gap={0.5}>
                <Tooltip title="미리보기">
                  <IconButton size="small" onClick={handleFilePreview}>
                    <Visibility fontSize="small" />
                  </IconButton>
                </Tooltip>
                {!disabled && (
                  <Tooltip title="삭제">
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={() => setDeleteDialogOpen(true)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
          </Box>
        ) : (
          <Box
            ref={dropZoneRef}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={4}
            border="2px dashed"
            borderColor={isDragOver ? 'primary.main' : 'grey.300'}
            borderRadius={1}
            bgcolor={isDragOver ? 'rgba(25, 118, 210, 0.04)' : 'grey.50'}
            sx={{
              position: 'relative',
              cursor: disabled || uploading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: disabled || uploading ? 'grey.300' : 'primary.main',
                bgcolor: disabled || uploading ? 'grey.50' : 'rgba(25, 118, 210, 0.04)'
              }
            }}
            onClick={() => {
              if (!disabled && !uploading && fileInputRef.current) {
                fileInputRef.current.click();
              }
            }}
          >
            <PictureAsPdf sx={{ 
              fontSize: 48, 
              color: isDragOver ? 'primary.main' : 'grey.400', 
              mb: 1,
              transition: 'color 0.2s ease'
            }} />
            <Typography 
              variant="body2" 
              color={isDragOver ? 'primary' : 'textSecondary'} 
              textAlign="center" 
              mb={2}
            >
              {isDragOver ? (
                <>PDF 파일을 놓아주세요</>
              ) : (
                <>
                  PDF 파일을 선택하거나 여기로 드래그하세요<br />
                  (최대 10MB)
                </>
              )}
            </Typography>

            {/* 드래그 오버 시 오버레이 */}
            {isDragOver && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(25, 118, 210, 0.1)',
                  borderRadius: 1,
                  pointerEvents: 'none'
                }}
              >
                <Box textAlign="center">
                  <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h6" color="primary">
                    PDF 파일을 놓아주세요
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* 업로드 버튼 */}
        {!disabled && (
          <Box mt={2}>
            <input
              ref={fileInputRef}
              accept="application/pdf"
              style={{ display: 'none' }}
              id={`pdf-upload-${documentType}`}
              type="file"
              onChange={handleFileUpload}
              disabled={uploading}
            />
            <label htmlFor={`pdf-upload-${documentType}`}>
              <Button
                variant={currentDocument?.url ? "outlined" : "contained"}
                component="span"
                startIcon={<CloudUpload />}
                disabled={uploading}
                fullWidth
              >
                {currentDocument?.url ? '파일 교체' : '파일 선택'}
              </Button>
            </label>
          </Box>
        )}
      </CardContent>

      {/* 삭제 확인 다이얼로그 */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>파일 삭제 확인</DialogTitle>
        <DialogContent>
          <Typography>
            이 문서를 삭제하시겠습니까?
          </Typography>
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            삭제된 파일은 복구할 수 없습니다.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            취소
          </Button>
          <Button
            onClick={handleFileDelete}
            color="error"
            disabled={deleting}
            variant="contained"
          >
            {deleting ? '삭제 중...' : '삭제'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default DocumentUpload;
